import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable, inject } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import {
    TeamMemberResponse,
    InsertTeamMember,
    UpdateTeamMember,
    AddTeamMemberByEmail,
    TeamAvailabilityDto,
    AssignMemberServices,
    StaffProfile,
    UpdateStaffProfile,
} from '@baboor/baboor-models'
import { ApiResponse } from '@baboor/common-models'
import { Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class TeamApiService {
    private http = inject(HttpClient)

    constructor(
        @Inject(BABOOR_ENVIRONMENT)
        private env: BaboorEnvironmentConfig,
    ) {}

    private get apiUrl(): string {
        return `${this.env.apiUrl}/v1/team`
    }

    getAll(params?: {
        organizationId?: string
        page?: number
        size?: number
        search?: string
        role?: string
    }): Observable<ApiResponse<TeamMemberResponse[]>> {
        let httpParams = new HttpParams()
        if (params?.organizationId)
            httpParams = httpParams.set('organizationId', params.organizationId)
        if (params?.page) httpParams = httpParams.set('page', params.page)
        if (params?.size) httpParams = httpParams.set('size', params.size)
        if (params?.search) httpParams = httpParams.set('search', params.search)
        if (params?.role) httpParams = httpParams.set('role', params.role)
        return this.http.get<ApiResponse<TeamMemberResponse[]>>(this.apiUrl, {
            params: httpParams,
        })
    }

    getById(id: string): Observable<ApiResponse<TeamMemberResponse>> {
        return this.http.get<ApiResponse<TeamMemberResponse>>(
            `${this.apiUrl}/${id}`,
        )
    }

    create(
        data: InsertTeamMember,
    ): Observable<ApiResponse<TeamMemberResponse>> {
        return this.http.post<ApiResponse<TeamMemberResponse>>(
            this.apiUrl,
            data,
        )
    }

    update(
        id: string,
        data: UpdateTeamMember,
    ): Observable<ApiResponse<TeamMemberResponse>> {
        return this.http.patch<ApiResponse<TeamMemberResponse>>(
            `${this.apiUrl}/${id}`,
            data,
        )
    }

    delete(id: string): Observable<ApiResponse<null>> {
        return this.http.delete<ApiResponse<null>>(`${this.apiUrl}/${id}`)
    }

    addByEmail(
        data: AddTeamMemberByEmail,
    ): Observable<ApiResponse<TeamMemberResponse>> {
        return this.http.post<ApiResponse<TeamMemberResponse>>(
            `${this.apiUrl}/add-by-email`,
            data,
        )
    }

    getAvailability(
        id: string,
    ): Observable<ApiResponse<TeamAvailabilityDto[]>> {
        return this.http.get<ApiResponse<TeamAvailabilityDto[]>>(
            `${this.apiUrl}/${id}/availability`,
        )
    }

    setAvailability(
        id: string,
        data: {
            availability: {
                dayOfWeek: number
                isAvailable: boolean
                startTime?: string
                endTime?: string
            }[]
        },
    ): Observable<ApiResponse<TeamAvailabilityDto[]>> {
        return this.http.put<ApiResponse<TeamAvailabilityDto[]>>(
            `${this.apiUrl}/${id}/availability`,
            data,
        )
    }

    getServices(id: string): Observable<ApiResponse<AssignMemberServices>> {
        return this.http.get<ApiResponse<AssignMemberServices>>(
            `${this.apiUrl}/${id}/services`,
        )
    }

    assignServices(
        id: string,
        data: AssignMemberServices,
    ): Observable<ApiResponse<AssignMemberServices>> {
        return this.http.post<ApiResponse<AssignMemberServices>>(
            `${this.apiUrl}/${id}/services`,
            data,
        )
    }

    getMyStaffProfile(): Observable<ApiResponse<StaffProfile>> {
        return this.http.get<ApiResponse<StaffProfile>>(
            `${this.apiUrl}/profile/me`,
        )
    }

    getStaffProfile(id: string): Observable<ApiResponse<StaffProfile>> {
        return this.http.get<ApiResponse<StaffProfile>>(
            `${this.apiUrl}/${id}/profile`,
        )
    }

    updateMyStaffProfile(
        data: UpdateStaffProfile,
    ): Observable<ApiResponse<StaffProfile>> {
        return this.http.patch<ApiResponse<StaffProfile>>(
            `${this.apiUrl}/profile/me`,
            data,
        )
    }

    updateStaffProfile(
        id: string,
        data: UpdateStaffProfile,
    ): Observable<ApiResponse<StaffProfile>> {
        return this.http.patch<ApiResponse<StaffProfile>>(
            `${this.apiUrl}/${id}/profile`,
            data,
        )
    }

    uploadStaffPhoto(
        file: File,
        teamMemberId?: string,
    ): Observable<ApiResponse<{ url: string }>> {
        const formData = new FormData()
        formData.append('file', file)
        if (teamMemberId) {
            formData.append('teamMemberId', teamMemberId)
        }
        return this.http.post<ApiResponse<{ url: string }>>(
            `${this.apiUrl}/profile/upload-photo`,
            formData,
        )
    }

    removeStaffPhoto(
        teamMemberId?: string,
    ): Observable<ApiResponse<{ success: boolean }>> {
        let params = new HttpParams()
        if (teamMemberId) {
            params = params.set('teamMemberId', teamMemberId)
        }
        return this.http.delete<ApiResponse<{ success: boolean }>>(
            `${this.apiUrl}/profile/photo`,
            { params },
        )
    }
}
